import { useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/stores/authStore";
import { enabledFeatures, isFeatureEnabled } from "@/services/featureFlags";
import { companySections, adminSections } from "@/layouts/navigation";
import { SidebarNav } from "@/layouts/Sidebar";
import { Topbar } from "@/layouts/Topbar";
import { VerifyEmailBanner } from "@/features/auth/VerifyEmailBanner";
import { BrandingApplier } from "@/components/BrandingApplier";
import { SessionManager } from "@/components/SessionManager";
import { Sheet, SheetContent } from "@/components/ui/sheet";

function buildSections(rawSections, { hasPermission, flags, isSuperAdmin }) {
  return rawSections
    .map((section) => {
      const items = rawSections === adminSections
        ? section.items // admin nav is not permission/flag gated
        : section.items.filter((i) => !i.perm || isSuperAdmin || hasPermission(i.perm));
      return {
        title: section.title,
        tKey: section.tKey,
        items: items.map((i) => ({
          ...i,
          key: i.to.split("/").pop() || "root",
          enabled: rawSections === adminSections ? true : isFeatureEnabled(flags, i.flag),
        })),
      };
    })
    .filter((s) => s.items.length > 0);
}

function activeTitle(sections, pathname, t) {
  let best = null;
  sections.forEach((s) =>
    s.items.forEach((i) => {
      if (i.end ? pathname === i.to : pathname.startsWith(i.to)) {
        if (!best || i.to.length > best.to.length) best = i;
      }
    })
  );
  return best ? (best.tKey ? t(`nav.${best.tKey}`, { defaultValue: best.label }) : best.label) : "Dashboard";
}

export function DashboardLayout({ variant = "company" }) {
  const { t } = useTranslation();
  const { ctx, hasPermission } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("arcquan.sidebar") === "collapsed");
  const toggleCollapsed = () => setCollapsed((c) => { localStorage.setItem("arcquan.sidebar", c ? "open" : "collapsed"); return !c; });

  const isSuperAdmin = !!ctx?.is_super_admin;
  const flags = useMemo(() => enabledFeatures(ctx?.tenant, isSuperAdmin), [ctx, isSuperAdmin]);

  const sections = useMemo(() => {
    const raw = variant === "admin" ? adminSections : companySections;
    return buildSections(raw, { hasPermission, flags, isSuperAdmin });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, ctx, flags]);

  const brand =
    variant === "admin"
      ? { title: t("shell.platform"), subtitle: t("shell.superAdminConsole") }
      : { title: ctx?.tenant?.name || t("shell.company"), subtitle: t("shell.companyPanel"), logo: ctx?.tenant?.branding?.logo_url };

  const title = activeTitle(sections, location.pathname, t);

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      {variant === "company" && <BrandingApplier branding={ctx?.tenant?.branding} name={ctx?.tenant?.name} />}
      <aside data-testid="desktop-sidebar" className={`hidden shrink-0 border-r border-border/60 transition-[width] duration-200 lg:block ${collapsed ? "w-[68px]" : "w-64"}`}>
        <div className="sticky top-0 h-screen">
          <SidebarNav brand={brand} sections={sections} collapsed={collapsed} onToggle={toggleCollapsed} />
        </div>
      </aside>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-72 border-border/60 bg-card p-0" data-testid="mobile-sidebar">
          <SidebarNav brand={brand} sections={sections} onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar onMenu={() => setMobileOpen(true)} title={title} />
        {variant === "company" && <VerifyEmailBanner />}
        <main className="flex-1 animate-in fade-in duration-500">
          <div className="mx-auto w-full max-w-[1600px] px-5 py-7 sm:px-8 sm:py-9 lg:px-12 lg:py-10">
            <Outlet />
            <SessionManager />
          </div>
        </main>
      </div>
    </div>
  );
}
